import { getCookieData, IS_PRODUCTION } from '@repo/common-utils';
import { useCommonRouter, useWindowOpen } from '@repo/platform';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { Description, Divider, useDialog } from '@repo/ui';
import { useAuthContext, saveReturnUrl, BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY } from '@repo/user-session';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { BZNAV_LAST_SIGN_IN_COOKIE_KEY, SIGN_IN_ERRORS, SIGN_IN_PAGE_CONTENT } from '../constants';
import { SignInErrors, SignItemType, SocialItemType } from '../types';

import { EmailSignInForm, EmailTextButtonSection, SocialCircleButton, SocialRectangleButton } from './SignItems';

export const SignInContent = ({ signItems = ['kakao', 'naver', 'apple', 'email'] }: { signItems?: SignItemType[] }) => {
  const router = useCommonRouter();
  const { auth, authService } = useAuthContext();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const { openWindow } = useWindowOpen();
  const appId = authService?.appId ?? 'bznav-refund';
  const filteredSignItems = useRef(IS_PRODUCTION || process.env.NEXT_PUBLIC_ZENV === 'stg' ? signItems.filter((item) => item !== 'email') : signItems);
  const lastSignInItem = getCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY);
  const [mainItem, setMainItem] = useState<SignItemType>(
    (lastSignInItem && filteredSignItems.current.includes(lastSignInItem) ? lastSignInItem : signItems[0]) ?? 'kakao'
  );

  const showErrorDialog = useCallback(
    async (errorCode: SignInErrors) => {
      const dialogData = SIGN_IN_ERRORS[errorCode] ?? SIGN_IN_ERRORS.sign_failed;

      sendViewEvent('sign-up-error_modal', { errorCode }, { isAllowRetry: true });
      const userResponse = await showDialog({
        id: errorCode,
        ...dialogData
      });

      let errorEventName = 'sign-up-error_modal_ok';
      let returnUrl = router.pathname;
      switch (errorCode) {
        case 'kakao_email_required':
          errorEventName = userResponse.selected === 'primary' ? 'sign-up-error_modal_manage-account' : 'sign-up-error_modal_close';
          break;
        case 'under_age_signup':
          errorEventName = 'sign-up-error_modal_back';
          returnUrl = '/';
          break;
      }
      sendClickEvent(errorEventName, { errorCode });
      if (errorCode === 'kakao_email_required' && userResponse.selected === 'primary') {
        openWindow('https://accounts.kakao.com', '_system');
      }
      router.replace(returnUrl);
    },
    [openWindow, router, sendClickEvent, sendViewEvent, showDialog]
  );

  useEffect(() => {
    if (router.query?.ext_id) {
      sessionStorage.setItem(BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY, router.query.ext_id as string);
    }
  }, [router.query.ext_id]);

  useEffect(() => {
    if (router.query?.return_url) {
      const returnUrl = router.query.return_url as string;
      saveReturnUrl(returnUrl);
    }
  }, [router.query.return_url]);

  useEffect(() => {
    if (authService && auth?.status === 'login') {
      router.replace(authService.signService.signInSuccessUrl);
    }
  }, [auth?.status, authService, authService?.signService.signInSuccessUrl, router]);

  useEffect(() => {
    if (!router.isReady) return;

    const errorCode = router.query?.error_code;
    if (errorCode) {
      showErrorDialog(errorCode as SignInErrors);
    }
  }, [router, showErrorDialog]);

  return (
    <>
      <Description
        graphic={
          <div className="justify-center relative flex w-[204px] py-4">
            <img alt="bznav logo" src={SIGN_IN_PAGE_CONTENT[appId].imageUrl} className={'object-contain h-10'} />
          </div>
        }
        title={SIGN_IN_PAGE_CONTENT[appId]?.title}
        description={SIGN_IN_PAGE_CONTENT[appId]?.subTitle}
        button={mainItem !== 'email' ? <SocialRectangleButton item={mainItem} lastSignInItem={lastSignInItem} /> : null}
      />
      <SubSignInItemSection signItems={filteredSignItems.current} mainItem={mainItem} setMainItem={setMainItem} />
      <PageViewEventLogger pageName={'sign-in'} eventOption={{ isUseAirbridge: true }} />
    </>
  );
};

const SubSignInItemSection = ({
  signItems,
  mainItem,
  setMainItem
}: {
  signItems: SignItemType[];
  mainItem: SignItemType;
  setMainItem: Dispatch<SetStateAction<SignItemType>>;
}) => {
  const subItems = signItems.filter((item) => item !== mainItem && item !== 'email') as SocialItemType[];
  const isUseEmailSignIn = signItems.includes('email');

  return (
    <>
      {mainItem === 'email' && <EmailSignInForm />}
      {(subItems.length > 0 || isUseEmailSignIn) && (
        <div className={`relative flex items-center justify-center py-[9px] mb-normal-large`}>
          <Divider className="w-full" />
          <div className={'absolute flex px-normal-small items-center justify-center bg-white caption-xsmall-regular text-neutral-foreground-lowest'}>또는</div>
        </div>
      )}
      {subItems.length > 0 && (
        <div className="flex items-center justify-center gap-x-[20px]">
          {subItems.map((item) => (
            <SocialCircleButton key={item} item={item} />
          ))}
        </div>
      )}
      {isUseEmailSignIn && <EmailTextButtonSection isShowLoginButton={mainItem !== 'email'} loginButtonAction={() => setMainItem('email')} />}
    </>
  );
};
